// Remembers which `result_key` a chart resolved to under a given dashboard +
// filter state, across sessions.
//
// Why it has to exist: the persisted query cache (`cache-persist.ts`) keys
// chart RESULTS by `result_key` alone (see `queries.ts`'s `biKeys.result` —
// the key is a pure function of the compiled query, so two charts that reduce
// to the same query share one entry). The client never computes that key; the
// server hands it back on `POST /dashboards/{id}/query`. So on a fresh open
// the persisted results are all sitting in the cache with no way to tell WHICH
// tile each one belongs to until the POST answers — which is the very round
// trip the persistence is there to paint through.
//
// This map closes that gap: (dashboard, filters, chart) -> result_key, written
// whenever a response or a settle event reveals one, read at first paint. It
// holds no data, only keys, so a stale entry can at worst point at a cache
// entry that is missing or expired, which paints nothing and costs nothing.
import { PERSIST_MAX_AGE_MS, type PersistStorage } from "./cache-persist";

export interface ResultKeyEntry {
  resultKey: string;
  /** When this association was last observed, for the age filter + eviction. */
  at: number;
}

export type ResultKeyMap = Readonly<Record<string, ResultKeyEntry>>;

export interface ResultKeyLookup {
  dashboardId: string;
  /** The same stable serialization `bi-query.ts` keys its effect on
   *  (`JSON.stringify(filters)`), never the raw object — object identity
   *  changes every render. */
  filtersKey: string;
  chartId: string;
}

const STORAGE_KEY = "bi:result-keys";
const ENVELOPE_VERSION = 1;
/** ~45 charts on the biggest board, a handful of filter states each. Bounds
 *  the entry to well under a hundred KB of localStorage. */
export const MAX_REMEMBERED_KEYS = 1_000;

/** djb2. The filter state serializes to an arbitrarily long JSON string and
 *  only ever needs to be compared for equality here, so it is hashed rather
 *  than stored — a collision costs one tile a wrong-but-expired lookup that
 *  paints nothing, never a wrong number (the result itself is keyed by the
 *  server's own query hash). */
export function hashKeyPart(value: string): string {
  let hash = 5381;
  for (let i = 0; i < value.length; i += 1) {
    hash = ((hash << 5) + hash + value.charCodeAt(i)) | 0;
  }
  return (hash >>> 0).toString(36);
}

export function resultKeyCacheKey({ dashboardId, filtersKey, chartId }: ResultKeyLookup): string {
  return `${dashboardId}:${hashKeyPart(filtersKey)}:${chartId}`;
}

export function lookupResultKey(
  map: ResultKeyMap,
  lookup: ResultKeyLookup,
  { now, maxAgeMs = PERSIST_MAX_AGE_MS }: { now: number; maxAgeMs?: number },
): string | undefined {
  const entry = map[resultKeyCacheKey(lookup)];
  if (!entry) return undefined;
  if (now - entry.at > maxAgeMs) return undefined;
  return entry.resultKey;
}

export interface RememberedKey extends ResultKeyLookup {
  resultKey: string;
}

/** Returns a NEW map with these associations recorded, aged entries dropped,
 *  and the oldest evicted down to `limit`. Pure. */
export function rememberResultKeys(
  map: ResultKeyMap,
  entries: readonly RememberedKey[],
  {
    now,
    maxAgeMs = PERSIST_MAX_AGE_MS,
    limit = MAX_REMEMBERED_KEYS,
  }: { now: number; maxAgeMs?: number; limit?: number },
): ResultKeyMap {
  const next: Record<string, ResultKeyEntry> = {};
  for (const [key, entry] of Object.entries(map)) {
    if (now - entry.at <= maxAgeMs) next[key] = entry;
  }
  for (const entry of entries) {
    if (!entry.resultKey) continue;
    next[resultKeyCacheKey(entry)] = { resultKey: entry.resultKey, at: now };
  }

  const keys = Object.keys(next);
  if (keys.length <= limit) return next;
  // Oldest out first.
  const ordered = keys.sort((a, b) => next[b]!.at - next[a]!.at).slice(0, limit);
  const capped: Record<string, ResultKeyEntry> = {};
  for (const key of ordered) capped[key] = next[key]!;
  return capped;
}

interface Envelope {
  version: number;
  entries: Record<string, ResultKeyEntry>;
}

/** Parse a stored envelope. `{}` for anything absent, unparseable, or from
 *  another version — a cold start, not an error. Pure. */
export function parseResultKeyMap(raw: string | null): ResultKeyMap {
  if (!raw) return {};
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return {};
  }
  if (typeof parsed !== "object" || parsed === null) return {};
  const envelope = parsed as Partial<Envelope>;
  if (envelope.version !== ENVELOPE_VERSION) return {};
  if (typeof envelope.entries !== "object" || envelope.entries === null) return {};
  const out: Record<string, ResultKeyEntry> = {};
  for (const [key, value] of Object.entries(envelope.entries)) {
    if (
      typeof value === "object" &&
      value !== null &&
      typeof (value as ResultKeyEntry).resultKey === "string" &&
      typeof (value as ResultKeyEntry).at === "number"
    ) {
      out[key] = {
        resultKey: (value as ResultKeyEntry).resultKey,
        at: (value as ResultKeyEntry).at,
      };
    }
  }
  return out;
}

export function serializeResultKeyMap(map: ResultKeyMap): string {
  return JSON.stringify({ version: ENVELOPE_VERSION, entries: map } satisfies Envelope);
}

function browserStorage(): PersistStorage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function loadResultKeyMap(storage: PersistStorage | null = browserStorage()): ResultKeyMap {
  if (!storage) return {};
  try {
    return parseResultKeyMap(storage.getItem(STORAGE_KEY));
  } catch {
    return {};
  }
}

export function saveResultKeyMap(
  map: ResultKeyMap,
  storage: PersistStorage | null = browserStorage(),
): void {
  if (!storage) return;
  try {
    storage.setItem(STORAGE_KEY, serializeResultKeyMap(map));
  } catch {
    // Optional cache, same posture as cache-persist.ts: a quota failure means
    // the next open re-fetches, nothing more.
  }
}
